/**
 * Buildings in realistic pixel art. Every face is painted pixel by pixel from a material — lime
 * plaster stained by damp and rain, grey brick, cut stone, grained timber, red lacquer, barrel
 * roof tiles with their drip caps and ridge — each on its own hue-shifted ramp, lit by the key
 * light from the upper right like the figures and the ground (front-right faces in sun, front-left
 * in shade, eaves casting a band of shadow on the walls), and edged with a selective outline.
 *
 * Sprites keep the footprints, sizes and anchors of the map's structures: the anchor is the
 * north corner of the footprint, and `P(x, y, z)` projects footprint units and height in pixels.
 */
import { hash2, valueNoise } from '@nine/shared';
import { RAMPS as GROUND } from './groundArt';
import { canvas, ctx2d, selOutline } from './pixel';
import type { Sprite } from './scenery';
import { CURVE, curvedRoof } from './kit/curvedRoof';

export type RGB = readonly [number, number, number];
export type Pt = readonly [number, number];
export type Proj = (x: number, y: number, z: number) => Pt;
export type Ramp = readonly RGB[];
/** A material: texture coordinates in pixels (u along, v up) and the light falling on it, 0..1. */
export type Tex = (u: number, v: number, l: number) => RGB | null;

const ramp = (...hex: string[]): Ramp =>
  hex.map(
    (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)] as const,
  );

/** Material ramps, deep shadow (cool) to full sun (warm). */
export const M = {
  plaster: ramp('#5e5348', '#7d6f60', '#9c8c77', '#bba98e', '#d6c6a6', '#ece0c2'),
  timber: ramp('#1e130d', '#2f1e14', '#452c1e', '#5c3d2a', '#77513a'),
  wood: ramp('#2a1a10', '#40281a', '#583826', '#714a32', '#8c6042', '#a87a56'),
  deck: ramp('#3a2414', '#5a3a22', '#7a5434', '#9a7048', '#b88c5e', '#d4aa78'),
  lacquer: ramp('#360f0d', '#541813', '#76221a', '#9a2f22', '#bc432e', '#d9684a'),
  stone: GROUND.stone,
  brick: ramp('#33302e', '#4a4744', '#625e59', '#7b766f', '#958f86', '#aca69c'),
  slate: ramp('#11161f', '#1b2230', '#283242', '#384556', '#4e5e70', '#6c7e90'),
  terra: ramp('#2a100b', '#451a12', '#64281a', '#843626', '#a24a34', '#c06a4c'),
  jade: ramp('#0c1f1a', '#14332a', '#1d4a3c', '#29634f', '#3a7e64', '#57a080'),
  gold: ramp('#4a2e10', '#7a5220', '#a87a30', '#d4a848', '#f2d488'),
  paper: ramp('#2a1e16', '#5a4630', '#8e7450', '#c0a270', '#e2c992'),
  red: ramp('#4a0f0c', '#7a1a14', '#a8281e', '#cc3e2c', '#e86a4a'),
  blue: ramp('#141e34', '#1f3050', '#2e466e', '#42608e', '#5c7eaa'),
  linen: ramp('#8a8068', '#b0a68a', '#d2c8a8', '#ece4c8', '#faf4e0'),
  tar: ramp('#140e0a', '#22170f', '#322216', '#44301f'),
  net: ramp('#4e5a58', '#6e7c7a', '#90a09c', '#b4c2bc'),
  moss: GROUND.moss,
  dark: ramp('#0e0a08', '#1a120e', '#261a14'),
} as const;

/**
 * Light on the front-left faces, the front-right faces and the tops. The key light is up and to
 * the right, so the front-right faces take the sun; the front-left ones are in shade but kept a
 * readable mid tone (doors, signs and windows on them stay clear), not a muddy dark.
 */
export const LEFT = 0.44;
export const RIGHT = 0.74;
export const TOP = 0.8;

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** A step of a ramp for light `l`, dithered between neighbouring steps. */
export function snap(r: Ramp, l: number, x: number, y: number, dither = 0.45): RGB {
  const b = (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16 - 0.5;
  const i = Math.round(clamp01(l) * (r.length - 1) + b * dither);
  return r[i < 0 ? 0 : i >= r.length ? r.length - 1 : i]!;
}

/** The sprite's pixels, painted directly. */
export class Pix {
  readonly d: Uint8ClampedArray<ArrayBuffer>;
  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.d = new Uint8ClampedArray(w * h * 4);
  }
  set(x: number, y: number, c: RGB): void {
    x = Math.floor(x);
    y = Math.floor(y);
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const k = (y * this.w + x) * 4;
    this.d[k] = c[0];
    this.d[k + 1] = c[1];
    this.d[k + 2] = c[2];
    this.d[k + 3] = 255;
  }
  clear(x: number, y: number): void {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    this.d[(y * this.w + x) * 4 + 3] = 0;
  }
  rect(x: number, y: number, w: number, h: number, c: RGB): void {
    for (let b = 0; b < h; b++) for (let a = 0; a < w; a++) this.set(x + a, y + b, c);
  }
  toCanvas(): HTMLCanvasElement {
    const c = canvas(this.w, this.h);
    ctx2d(c).putImageData(new ImageData(this.d, this.w, this.h), 0, 0);
    return c;
  }
}

/** The finished sprite: pixels to a canvas with a selective outline. */
export function finish(px: Pix, ax: number, ay: number): Sprite {
  return { img: selOutline(px.toCanvas(), '#120d0a'), ax, ay };
}

/**
 * Paints the quad a→b (bottom edge, along s), d→c (top edge) by forward mapping, so any
 * parallelogram or trapezoid fills without holes. `fn(s, t, S, T)` gets pixels along each way.
 */
export function quad(
  px: Pix,
  a: Pt,
  b: Pt,
  c: Pt,
  d: Pt,
  fn: (s: number, t: number, S: number, T: number) => RGB | null,
): void {
  const S = Math.max(Math.hypot(b[0] - a[0], b[1] - a[1]), Math.hypot(c[0] - d[0], c[1] - d[1]), 1);
  const T = Math.max(Math.hypot(d[0] - a[0], d[1] - a[1]), Math.hypot(c[0] - b[0], c[1] - b[1]), 1);
  const ns = Math.ceil(S * 2);
  const nt = Math.ceil(T * 2);
  for (let i = 0; i <= ns; i++) {
    const s = i / ns;
    const ex = a[0] + (b[0] - a[0]) * s;
    const ey = a[1] + (b[1] - a[1]) * s;
    const fx = d[0] + (c[0] - d[0]) * s;
    const fy = d[1] + (c[1] - d[1]) * s;
    for (let k = 0; k <= nt; k++) {
      const t = k / nt;
      const col = fn(s * S, t * T, S, T);
      if (col) px.set(ex + (fx - ex) * t, ey + (fy - ey) * t, col);
    }
  }
}

/** Fills a polygon, `fn(x, y)` choosing each pixel's colour. */
export function fill(px: Pix, pts: readonly Pt[], fn: (x: number, y: number) => RGB | null): void {
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  for (let y = Math.floor(Math.min(...ys)); y <= Math.ceil(Math.max(...ys)); y++)
    for (let x = Math.floor(Math.min(...xs)); x <= Math.ceil(Math.max(...xs)); x++)
      if (inside(pts, x + 0.5, y + 0.5)) {
        const c = fn(x, y);
        if (c) px.set(x, y, c);
      }
}
export function inside(pts: readonly Pt[], x: number, y: number): boolean {
  let on = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]!;
    const [xj, yj] = pts[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) on = !on;
  }
  return on;
}
/** A line of pixels from a to b, `w` wide, `fn(f)` colouring each by how far along it is (0..1). */
export function line(px: Pix, a: Pt, b: Pt, fn: (f: number, k: number) => RGB, w = 1): void {
  const n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) * 2));
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const x = a[0] + (b[0] - a[0]) * f;
    const y = a[1] + (b[1] - a[1]) * f;
    for (let k = 0; k < w; k++) px.set(x, y + k, fn(f, k));
  }
}

// ───────────────────────── faces ─────────────────────────

/** C0-ROTATE: a structural face as a painter draws it: corners in footprint units, height in art pixels. */
export type Face3 = { readonly pts: readonly (readonly [number, number, number])[]; readonly kind: 'wall' | 'top' | 'roof' };
/**
 * C0-ROTATE (route C, docs/research/C0_CAMERA.md §6): while `on`, `wall`, `top` and `roof` note the faces they paint,
 * so a turned camera can lay the painted sprite back onto them and draw a building from any angle (as apps/c0-rotate).
 */
export const faceRec: { on: boolean; faces: Face3[] } = { on: false, faces: [] };

export type Side = 'left' | 'right';
/**
 * A vertical face on the front-left (`y = at`) or front-right (`x = at`) side, from u0 to u1 along
 * it and z0 to z1 up. The texture gets pixels along the face (16 a tile) and the height.
 */
export function wall(
  px: Pix,
  P: Proj,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
  tex: Tex,
  light?: number,
): void {
  const l = light ?? (side === 'left' ? LEFT : RIGHT);
  const Q = (u: number, z: number): Pt => (side === 'left' ? P(u, at, z) : P(at, u, z));
  if (faceRec.on) {
    const R = (u: number, z: number) => (side === 'left' ? ([u, at, z] as const) : ([at, u, z] as const));
    faceRec.faces.push({ kind: 'wall', pts: [R(u0, z0), R(u1, z0), R(u1, z1), R(u0, z1)] });
  }
  quad(px, Q(u0, z0), Q(u1, z0), Q(u1, z1), Q(u0, z1), (s, t, S) =>
    tex(u0 * 16 + (s / S) * (u1 - u0) * 16, z0 + t, l),
  );
}
/** A level top from (x0, y0) to (x1, y1) at height z; the texture gets pixels across and down it. */
export function top(
  px: Pix,
  P: Proj,
  [x0, y0, x1, y1]: readonly number[],
  z: number,
  tex: Tex,
  light = TOP,
): void {
  if (faceRec.on)
    faceRec.faces.push({ kind: 'top', pts: [[x0!, y1!, z], [x1!, y1!, z], [x1!, y0!, z], [x0!, y0!, z]] });
  quad(px, P(x0!, y1!, z), P(x1!, y1!, z), P(x1!, y0!, z), P(x0!, y0!, z), (s, t, S, T) =>
    tex(x0! * 16 + (s / S) * (x1! - x0!) * 16, y1! * 16 - (t / T) * (y1! - y0!) * 16, light),
  );
}
/** A box: its front-left, front-right and top faces in one material (or one per face). */
export function box(
  px: Pix,
  P: Proj,
  r: readonly [number, number, number, number],
  z0: number,
  z1: number,
  tex: Tex,
  topTex: Tex = tex,
): void {
  const [x0, y0, x1, y1] = r;
  wall(px, P, 'left', y1, x0, x1, z0, z1, tex);
  wall(px, P, 'right', x1, y0, y1, z0, z1, tex);
  top(px, P, r, z1, topTex);
}

// ───────────────────────── materials ─────────────────────────

/** Shadow the eaves cast across a wall below height `eave`. */
export const under = (v: number, eave: number): number => (v > eave - 5 ? 0.36 : v > eave - 10 ? 0.18 : 0);

/** Lime plaster: mottled, damp creeping up from `foot`, rain streaks, darker under the eaves. */
export const plaster =
  (foot: number, eave: number): Tex =>
  (u, v, l) => {
    l += (valueNoise(u / 9, v / 14) - 0.5) * 0.14 + (hash2(u | 0, v | 0) - 0.5) * 0.05;
    if (v - foot < 10) l -= (1 - (v - foot) / 10) * 0.22;
    if (valueNoise(u / 2.5 + 7, v / 26) > 0.78) l -= 0.08;
    return snap(M.plaster, l - under(v, eave), u | 0, v | 0);
  };

/** Grey brick in courses 4px high, every other course offset by half a brick. */
export const brick =
  (eave = 1e9): Tex =>
  (u, v, l) => {
    const row = Math.floor(v / 4);
    const off = row & 1 ? 4 : 0;
    const b = Math.floor((u + off) / 8);
    l -= under(v, eave);
    if (v % 4 < 1 || (u + off) % 8 < 1) return snap(M.stone, l + 0.08, u | 0, v | 0, 0.2);
    l += (hash2(b * 7, row * 13) - 0.5) * 0.28 + (v % 4 >= 3 ? 0.08 : 0);
    return snap(M.brick, l, u | 0, v | 0);
  };

/** Cut stone in courses: each block lit along its top, shadowed along its foot. */
export const ashlar: Tex = (u, v, l) => {
  const row = Math.floor(v / 5);
  const off = row & 1 ? 6 : 0;
  const b = Math.floor((u + off) / 12);
  if (v % 5 < 0.9 || (u + off) % 12 < 0.9) return snap(M.stone, l - 0.32, u | 0, v | 0, 0);
  l += (hash2(b * 5, row * 11) - 0.5) * 0.24 + (v % 5 > 4 ? 0.12 : v % 5 < 1.8 ? -0.1 : 0);
  if (hash2(u | 0, v | 0) > 0.97) l -= 0.18;
  return snap(M.stone, l, u | 0, v | 0);
};
/** Flagged stone for platform tops. */
export const flags: Tex = (u, v, l) => {
  const row = Math.floor(v / 8);
  const b = Math.floor((u + (row & 1) * 7) / 14);
  if (v % 8 < 1 || (u + (row & 1) * 7) % 14 < 1) return snap(M.stone, l - 0.3, u | 0, v | 0, 0);
  return snap(
    M.stone,
    l + (hash2(b * 3, row * 7) - 0.5) * 0.2 + (valueNoise(u / 4, v / 4) - 0.5) * 0.1,
    u | 0,
    v | 0,
  );
};

/** Wood, grained along its length: boards `w` px wide standing up (or lying, `lying`). */
export const boards =
  (r: Ramp, w = 5, lying = false, eave = 1e9): Tex =>
  (u, v, l) => {
    const [a, b] = lying ? [v, u] : [u, v];
    const board = Math.floor(a / w);
    if (a % w < 1) return snap(r, l - 0.3 - under(v, eave), u | 0, v | 0, 0);
    l += (valueNoise(a * 0.6 + board * 13, b / 7) - 0.5) * 0.26 + (hash2(board, 3) - 0.5) * 0.14;
    if (hash2(board * 7, Math.floor(b / 9)) > 0.96 && (b | 0) % 9 === 4) l -= 0.3; // a knot
    return snap(r, l - under(v, eave), u | 0, v | 0);
  };

/** Solid lacquer or paint, with a lit edge and a soft sheen. */
export const paint =
  (r: Ramp, eave = 1e9): Tex =>
  (u, v, l) =>
    snap(r, l + (valueNoise(u / 6, v / 10) - 0.5) * 0.1 - under(v, eave), u | 0, v | 0);

// ───────────────────────── parts ─────────────────────────

/** A square post or column from z0 to z1, `r` across (tile units). */
export function post(
  px: Pix,
  P: Proj,
  x: number,
  y: number,
  r: number,
  z0: number,
  z1: number,
  mat: Ramp,
  eave = 1e9,
): void {
  box(px, P, [x - r, y - r, x + r, y + r], z0, z1, (u, v, l) => {
    // The edges toward the key light: the near corner of the front-left face, the far edge of the front-right one.
    const edge = Math.abs(u - (x + r) * 16) < 1.2 || Math.abs(u - (y - r) * 16) < 1.2;
    return snap(
      mat,
      l + (edge ? 0.18 : 0) + (valueNoise(u / 3, v / 12) - 0.5) * 0.1 - under(v, eave),
      u | 0,
      v | 0,
    );
  });
}

/** A timber frame on a plastered wall: posts at each tile and beams at the given heights. */
export function frame(
  px: Pix,
  P: Proj,
  side: Side,
  at: number,
  n: number,
  z0: number,
  z1: number,
  beams: number[],
  mat: Ramp = M.timber,
  eave = 1e9,
): void {
  for (const z of beams) wall(px, P, side, at, 0, n, z - 1.5, z + 1.5, boards(mat, 32, true, eave));
  for (let u = 0; u <= n; u++) {
    const a = Math.max(0, u - 0.09);
    const b = Math.min(n, u + 0.09);
    wall(px, P, side, at, a, b, z0, z1, (uu, v, l) => {
      // The edge toward the key light: the right one on screen (high u on a front-left face, low on a front-right one).
      const lit = (side === 'left' ? b * 16 - uu : uu - a * 16) < 1.2 ? 0.16 : 0;
      return snap(mat, l + lit + (valueNoise(uu, v / 8) - 0.5) * 0.2 - under(v, eave), uu | 0, v | 0);
    });
  }
}

/** A lattice window: a timber frame round paper panes divided by a grid of slender bars, and a sill. */
export function lattice(
  px: Pix,
  P: Proj,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
  glow = 0.45,
  eave = 1e9,
): void {
  const a = u0 * 16;
  const b = u1 * 16;
  wall(px, P, side, at, u0 - 0.04, u1 + 0.04, z0 - 2, z0, (u, v, l) => snap(M.stone, l + 0.2, u | 0, v | 0));
  wall(px, P, side, at, u0, u1, z0, z1, (u, v, l) => {
    const sh = under(v, eave);
    if (u - a < 1.6 || b - u < 1.6 || v - z0 < 1.6 || z1 - v < 1.6)
      return snap(M.timber, l + 0.1 - sh, u | 0, v | 0);
    if (Math.round(u - a) % 4 === 0 || Math.round(v - z0) % 4 === 0)
      return snap(M.timber, l - 0.1 - sh, u | 0, v | 0, 0);
    // Paper panes, the light behind them strongest in the middle.
    const mid = 1 - Math.abs((u - a) / (b - a) - 0.5) - Math.abs((v - z0) / (z1 - z0) - 0.5);
    return snap(M.paper, glow + mid * 0.35 - sh * 0.6, u | 0, v | 0);
  });
}

/** A double door of studded planks in a dark frame, its threshold stone below. */
export function door(
  px: Pix,
  P: Proj,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
  mat: Ramp = M.wood,
): void {
  const a = u0 * 16;
  const b = u1 * 16;
  const m = (a + b) / 2;
  wall(px, P, side, at, u0 - 0.1, u1 + 0.1, z0, z1 + 4, (u, v, l) => snap(M.timber, l - 0.1, u | 0, v | 0));
  wall(px, P, side, at, u0, u1, z0, z1, (u, v, l) => {
    if (Math.abs(u - m) < 0.8) return M.dark[1]!;
    if ((v - z0) % 14 < 1.4 && Math.round(u - a) % 5 === 2) return snap(M.gold, 0.6, u | 0, v | 0, 0); // studs
    return boards(mat, 4)(u, v, l - 0.08);
  });
}

/** A red paper couplet hung beside a door, with faint gold brushwork. */
export function couplet(
  px: Pix,
  P: Proj,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
): void {
  wall(px, P, side, at, u0, u1, z0, z1, (u, v, l) => {
    if ((Math.round(v) % 7 === 3 || Math.round(v) % 7 === 4) && hash2(u | 0, v | 0) > 0.35)
      return snap(M.gold, 0.55, u | 0, v | 0, 0);
    return snap(M.red, l + 0.15 + (valueNoise(u, v / 6) - 0.5) * 0.12, u | 0, v | 0);
  });
}

/** A signboard: lacquer within a gold border and three gold characters. */
export function plaque(
  px: Pix,
  P: Proj,
  side: Side,
  at: number,
  u0: number,
  u1: number,
  z0: number,
  z1: number,
  field: Ramp = M.dark,
): void {
  const a = u0 * 16;
  const b = u1 * 16;
  wall(px, P, side, at, u0, u1, z0, z1, (u, v, l) => {
    if (u - a < 1.5 || b - u < 1.5 || v - z0 < 1.5 || z1 - v < 1.5)
      return snap(M.gold, l + 0.15, u | 0, v | 0);
    const k = ((u - a) / (b - a)) * 3;
    const cx = Math.abs((k % 1) - 0.5);
    const cy = Math.abs((v - z0) / (z1 - z0) - 0.5);
    if (cx < 0.22 && cy < 0.28 && hash2(Math.floor(k) * 9 + Math.round(u), Math.round(v)) > 0.3)
      return snap(M.gold, 0.7, u | 0, v | 0, 0);
    return snap(field, l + 0.2, u | 0, v | 0);
  });
}

/** A red paper lantern hanging from a cord at `p`, with its highlight and gold cap. */
export function lantern(px: Pix, p: Pt, drop = 3): void {
  const [x, y] = [Math.round(p[0]), Math.round(p[1])];
  px.rect(x, y, 1, drop, M.dark[1]!);
  const cy = y + drop + 4;
  for (let b = -4; b <= 4; b++)
    for (let a = -3; a <= 3; a++) {
      if ((a * a) / 10 + (b * b) / 17 > 1) continue;
      const l = 0.55 + a * 0.09 - b * 0.05 + (b % 3 === 0 ? -0.15 : 0);
      px.set(x + a, cy + b, snap(M.red, l, x + a, cy + b, 0.2));
    }
  px.set(x + 1, cy - 2, M.red[4]!);
  px.rect(x - 1, cy - 5, 3, 1, M.gold[3]!);
  px.rect(x - 1, cy + 5, 3, 1, M.gold[2]!);
}

/**
 * A tiled roof from an eave rectangle up to a top rectangle (a point for a pyramid, a line for a
 * hip roof, the next storey's walls for a skirt): barrel tiles in rows down the slope with their
 * channels between, courses overlapping, round drip caps along the eave, lichen low down, a pale
 * fascia, upswept corners and a ridge with its end ornaments.
 */
export function roof(
  px: Pix,
  P: Proj,
  [x0, y0, x1, y1]: readonly [number, number, number, number],
  ze: number,
  [tx0, ty0, tx1, ty1]: readonly [number, number, number, number],
  zt: number,
  tiles: Ramp,
  edge: Ramp = M.stone,
  ridge = true,
): void {
  if (CURVE.on) {
    curvedRoof(px, P, [x0, y0, x1, y1], ze, [tx0, ty0, tx1, ty1], zt, tiles, edge, ridge);
    return;
  }
  const faces: [Pt, Pt, Pt, Pt, number][] = [
    // Slopes facing the key light (up and to the right) brightest: back-right, front-right.
    [P(x0, y0, ze), P(x1, y0, ze), P(tx1, ty0, zt), P(tx0, ty0, zt), 0.48],
    [P(x0, y1, ze), P(x0, y0, ze), P(tx0, ty0, zt), P(tx0, ty1, zt), 0.3],
    [P(x1, y0, ze), P(x1, y1, ze), P(tx1, ty1, zt), P(tx1, ty0, zt), 0.68],
    [P(x0, y1, ze), P(x1, y1, ze), P(tx1, ty1, zt), P(tx0, ty1, zt), 0.42],
  ];
  if (faceRec.on)
    for (const f of [
      [[x0, y0, ze], [x1, y0, ze], [tx1, ty0, zt], [tx0, ty0, zt]],
      [[x0, y1, ze], [x0, y0, ze], [tx0, ty0, zt], [tx0, ty1, zt]],
      [[x1, y0, ze], [x1, y1, ze], [tx1, ty1, zt], [tx1, ty0, zt]],
      [[x0, y1, ze], [x1, y1, ze], [tx1, ty1, zt], [tx0, ty1, zt]],
    ] as const)
      faceRec.faces.push({ kind: 'roof', pts: f });
  for (const [a, b, c, d, light] of faces)
    quad(px, a, b, c, d, (s, t, S, T) => {
      const x = Math.round(s);
      const y = Math.round(t);
      let l = light;
      // Drip caps: round tile ends along the eave.
      if (t < 2.6) return (x & 3) < 2 ? snap(tiles, l + 0.3, x, y, 0) : snap(tiles, l - 0.45, x, y, 0);
      const ph = x & 3;
      l += ph === 0 ? 0.24 : ph === 1 ? 0.1 : ph === 2 ? -0.06 : -0.34;
      const r = (t - 2.6) % 5;
      if (r < 1) l -= 0.22;
      else if (r > 4) l += 0.06;
      l += (valueNoise(s / 5, t / 9) - 0.5) * 0.12;
      // Lichen on the lower courses.
      if (t < T * 0.5 && ph !== 3 && hash2(x * 3 + 1, y * 7) > 0.992 - (1 - t / T) * 0.01)
        return snap(M.moss, 0.5 + l * 0.4, x, y, 0);
      if (S < 1) return null;
      return snap(tiles, l, x, y);
    });
  // The fascia along the eave on the two near sides.
  line(px, P(x0, y1, ze), P(x1, y1, ze), (_, k) => (k ? edge[0]! : edge[2]!), 2);
  line(px, P(x1, y0, ze), P(x1, y1, ze), (_, k) => (k ? edge[1]! : edge[edge.length - 2]!), 2);
  // Corners curling up.
  for (const [cx, cy, dx] of [
    [x0, y1, -1],
    [x1, y1, 0],
    [x1, y0, 1],
  ] as const) {
    const [ex, ey] = P(cx, cy, ze);
    for (let k = 0; k < 6; k++) {
      const q = k / 5;
      px.set(ex + dx * q * q * 4, ey - k, edge[k < 3 ? edge.length - 2 : edge.length - 1]!);
      px.set(ex + dx * q * q * 4 + 1, ey - k, edge[1]!);
    }
  }
  if (!ridge) return;
  if (tx0 !== tx1 || ty0 !== ty1) {
    line(px, P(tx0, ty0, zt), P(tx1, ty1, zt), (_, k) => tiles[[3, 1, 0][k]!]!, 3);
    // Ornaments curling up at the ridge ends.
    for (const [rx, ry] of [P(tx0, ty0, zt), P(tx1, ty1, zt)]) {
      px.rect(rx - 1, ry - 4, 3, 4, tiles[1]!);
      px.rect(rx - 1, ry - 5, 2, 1, tiles[3]!);
      px.set(rx + 1, ry - 6, tiles[2]!);
    }
  } else {
    const [rx, ry] = P(tx0, ty0, zt);
    px.rect(rx - 2, ry - 2, 5, 3, tiles[1]!);
  }
}

/** A stone plinth under a building, cut stone on its sides and flags on top. */
export function plinth(px: Pix, P: Proj, r: readonly [number, number, number, number], h: number): void {
  box(px, P, r, 0, h, ashlar, flags);
}

// ───────────────────────── buildings ─────────────────────────

const ROOFS = [M.slate, M.terra, M.jade] as const;

/**
 * Village house: a stone plinth, walls a head taller than a person (plaster in a timber frame;
 * a timber shop front on variant 1; grey brick below the plaster on variant 2), lattice windows, a
 * studded double door with red couplets and a lantern, and a hip roof with upswept eaves.
 */
function house(n: number, variant: number): Sprite {
  const Hw = 84;
  const Hr = 52;
  const W = n * 32 + 48;
  const H = n * 16 + Hw + Hr + 40;
  const px = new Pix(W, H);
  const ax = W / 2;
  const ay = Hw + Hr + 30;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  plinth(px, P, [-0.14, -0.14, n + 0.14, n + 0.14], 8);
  const eave = Hw;
  const brickTo = variant === 2 ? 40 : 8;
  for (const side of ['left', 'right'] as const) {
    if (variant === 2) wall(px, P, side, n, 0, n, 8, brickTo, brick(eave));
    wall(px, P, side, n, 0, n, brickTo, Hw, plaster(brickTo, eave));
  }
  if (variant === 1) wall(px, P, 'right', n, 0, n, 8, Hw, boards(M.wood, 5, false, eave));
  for (const side of ['left', 'right'] as const) {
    if (variant === 1 && side === 'right') continue;
    frame(
      px,
      P,
      side,
      n,
      n,
      brickTo,
      Hw,
      variant === 2 ? [brickTo + 1, Hw * 0.55, Hw - 5] : [10, Hw * 0.55, Hw - 5],
      M.timber,
      eave,
    );
  }
  const wz = Hw * 0.55;
  for (let u = 0; u < n; u++) lattice(px, P, 'left', n, u + 0.22, u + 0.78, wz - 26, wz - 5, 0.4, eave);
  // The front door, a step before it, couplets either side and a lantern.
  const d0 = n / 2 - 0.55;
  const d1 = n / 2 + 0.55;
  if (variant === 1) {
    // A shop: shutters propped open on a counter, a blue cloth sign beside.
    wall(px, P, 'right', n, 0.3, d0 - 0.3, 8, 30, boards(M.deck, 4, true));
    wall(px, P, 'right', n, 0.3, d0 - 0.3, 30, 58, (u, v) => snap(M.dark, 0.3 + (v - 30) / 60, u | 0, v | 0));
    wall(px, P, 'right', n, d1 + 0.85, n - 0.2, 20, 62, (u, v, l) =>
      snap(M.blue, l + 0.2 + (valueNoise(u / 2, v / 9) - 0.5) * 0.3, u | 0, v | 0),
    );
  }
  for (let u = 0; u < n; u++) {
    if (u + 0.8 > d0 - 0.4 && u + 0.2 < d1 + 0.4) continue;
    if (variant === 1 && u + 0.8 < d0) continue;
    lattice(px, P, 'right', n, u + 0.22, u + 0.78, wz - 26, wz - 5, 0.3, eave);
  }
  box(px, P, [n, d0 - 0.15, n + 0.32, d1 + 0.15], 0, 5, ashlar, flags);
  door(px, P, 'right', n, d0, d1, 8, 64);
  couplet(px, P, 'right', n, d0 - 0.3, d0 - 0.13, 20, 60);
  couplet(px, P, 'right', n, d1 + 0.13, d1 + 0.3, 20, 60);
  plaque(px, P, 'right', n, d0 + 0.15, d1 - 0.15, 68, 76);
  lantern(px, P(n + 0.05, d1 + 0.35, 74));
  if (variant === 0) {
    // Firewood stacked against the side wall, log ends showing.
    for (let row = 0; row < 3; row++)
      for (let k = 0; k < 4 - row; k++) {
        const [lx, ly] = P(0.25 + k * 0.17 + row * 0.08, n + 0.12, 10 + row * 5);
        for (let b = -2; b <= 2; b++)
          for (let a = -2; a <= 2; a++)
            if (a * a + b * b <= 5)
              px.set(
                lx + a,
                ly + b,
                a * a + b * b <= 1 ? M.deck[4]! : snap(M.deck, 0.45 - b * 0.08, lx + a, ly + b),
              );
      }
  } else if (variant === 2) {
    // A water jar by the door.
    const [jx, jy] = P(n + 0.3, d1 + 0.45, 0);
    for (let b = -12; b <= 0; b++)
      for (let a = -5; a <= 5; a++) {
        const r = 5 - Math.abs(b + 6) * 0.25;
        if (Math.abs(a) > r) continue;
        px.set(jx + a, jy + b, snap(M.terra, 0.5 - a * 0.07 - (b > -3 ? 0.15 : 0), jx + a, jy + b));
      }
    px.rect(jx - 3, jy - 13, 7, 1, M.terra[1]!);
  }
  const o = 0.6;
  roof(
    px,
    P,
    [-o, -o, n + o, n + o],
    Hw - 2,
    [1.1, n / 2, n - 1.1, n / 2],
    Hw + Hr,
    ROOFS[variant % ROOFS.length]!,
  );
  return finish(px, ax, ay);
}

/** Three-storey pagoda: red lacquered storeys, gold lattice, bracket bands, glazed skirts, a gold spire. */
export function pagoda(n: number): Sprite {
  const story = 62;
  const gap = 20;
  const tiers = 3;
  const topZ = 10 + tiers * (story + gap) + 70;
  const W = n * 32 + 56;
  const H = topZ + n * 16 + 20;
  const px = new Pix(W, H);
  const ax = W / 2;
  const ay = topZ + 6;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  plinth(px, P, [-0.3, -0.3, n + 0.3, n + 0.3], 10);
  const step = n * 0.12;
  for (let k = 0; k < tiers; k++) {
    const ins = k * step;
    const [a, b] = [ins, n - ins];
    const z0 = 10 + k * (story + gap);
    const z1 = z0 + story;
    for (const side of ['left', 'right'] as const) {
      wall(px, P, side, b, a, b, z0, z1, paint(M.lacquer, z1));
      // Columns at the corners and between the bays, a bracket band under the eaves.
      for (let q = 0; q <= 4; q++) {
        const u = a + ((b - a) * q) / 4;
        wall(px, P, side, b, Math.max(a, u - 0.08), Math.min(b, u + 0.08), z0, z1, (uu, v, l) =>
          snap(
            M.lacquer,
            l - 0.22 + ((side === 'left' ? uu % 16 > 14.7 : uu % 16 < 1.3) ? 0.2 : 0) - under(v, z1),
            uu | 0,
            v | 0,
          ),
        );
      }
      wall(px, P, side, b, a, b, z1 - 8, z1, (u, v, l) => {
        const blk = Math.floor(u / 5);
        if (u % 5 < 1) return M.dark[1]!;
        return snap(blk % 2 ? M.jade : M.gold, l + (v > z1 - 3 ? 0.15 : -0.05), u | 0, v | 0);
      });
      const mid = (a + b) / 2;
      if (side === 'right' && k === 0) door(px, P, side, b, mid - 0.45, mid + 0.45, z0, z1 - 14, M.lacquer);
      else lattice(px, P, side, b, mid - 0.5, mid + 0.5, z0 + 12, z1 - 14, 0.55, z1);
      // Smaller lattice in the outer bays.
      for (const f of [0.18, 0.82]) {
        const u = a + (b - a) * f;
        lattice(px, P, side, b, u - 0.22, u + 0.22, z0 + 18, z1 - 18, 0.45, z1);
      }
    }
    if (k) {
      // The balcony rail along each upper storey.
      for (const [s0, s1, side] of [
        [P(a - 0.15, b + 0.15, z0 + 8), P(b + 0.15, b + 0.15, z0 + 8), LEFT],
        [P(b + 0.15, a - 0.15, z0 + 8), P(b + 0.15, b + 0.15, z0 + 8), RIGHT],
      ] as const)
        line(px, s0, s1, (f) => snap(M.gold, side + 0.1, Math.round(f * 99), 0), 2);
    }
    const o = 0.65;
    const last = k === tiers - 1;
    const ni = (k + 1) * step;
    roof(
      px,
      P,
      [a - o, a - o, b + o, b + o],
      z1,
      last ? [n / 2, n / 2, n / 2, n / 2] : [ni, ni, n - ni, n - ni],
      last ? z1 + 46 : z1 + gap,
      M.jade,
      M.gold,
      last,
    );
  }
  // The spire: gold rings stacked on a stem, a pearl on top.
  const [sx, sy] = P(n / 2, n / 2, 10 + tiers * (story + gap) + 24);
  for (let k = 0; k < 7; k++) {
    const w = 7 - 2 * (k % 2);
    for (let a = 0; a < w; a++)
      px.rect(
        Math.round(sx) - 3 + (k % 2) + a,
        Math.round(sy) - k * 4,
        1,
        3,
        snap(M.gold, 0.85 - a * 0.1, a, k, 0),
      );
  }
  for (let b = -4; b <= 4; b++)
    for (let a = -4; a <= 4; a++)
      if (a * a + b * b <= 16) px.set(sx + a, sy - 32 + b, snap(M.gold, 0.95 - (a + b) * 0.07, a, b, 0.3));
  return finish(px, ax, ay);
}

/** Open pavilion: a stone platform with steps, red columns on stone bases, rails, a stone table, a pyramid roof. */
export function pavilion(n: number, variant: number): Sprite {
  const Hc = 76;
  const Hr = 44;
  const W = n * 32 + 48;
  const H = Hc + Hr + n * 16 + 50;
  const px = new Pix(W, H);
  const ax = W / 2;
  const ay = Hc + Hr + 40;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  plinth(px, P, [-0.1, -0.1, n + 0.1, n + 0.1], 10);
  // Steps up to the platform on the front.
  box(px, P, [n + 0.1, n / 2 - 0.5, n + 0.42, n / 2 + 0.5], 0, 4, ashlar, flags);
  box(px, P, [n + 0.1, n / 2 - 0.5, n + 0.26, n / 2 + 0.5], 4, 7, ashlar, flags);
  const e = 0.25;
  const column = (x: number, y: number) => {
    box(px, P, [x - 0.2, y - 0.2, x + 0.2, y + 0.2], 10, 14, ashlar, flags);
    post(px, P, x, y, 0.13, 14, 10 + Hc, M.lacquer, 10 + Hc);
  };
  column(e, e);
  column(n - e, e);
  column(e, n - e);
  // A stone table and two drum stools.
  box(px, P, [n / 2 - 0.12, n / 2 - 0.12, n / 2 + 0.12, n / 2 + 0.12], 10, 22, ashlar);
  box(px, P, [n / 2 - 0.4, n / 2 - 0.4, n / 2 + 0.4, n / 2 + 0.4], 22, 26, ashlar, flags);
  for (const [sx, sy] of [
    [n / 2 + 0.6, n / 2],
    [n / 2, n / 2 + 0.6],
  ] as const)
    box(px, P, [sx - 0.14, sy - 0.14, sx + 0.14, sy + 0.14], 10, 20, ashlar, flags);
  // Rails along the front sides: a top rail and balusters.
  for (const [side, at] of [
    ['left', n - e],
    ['right', n - e],
  ] as const) {
    wall(px, P, side, at, e, n - e, 30, 34, paint(M.lacquer));
    wall(px, P, side, at, e, n - e, 14, 30, (u, v, l) =>
      Math.round(u) % 6 < 2 ? snap(M.lacquer, l - 0.1, u | 0, v | 0) : null,
    );
  }
  column(n - e, n - e);
  // A painted beam under the eaves.
  for (const side of ['left', 'right'] as const)
    wall(px, P, side, n - e, e, n - e, 10 + Hc - 9, 10 + Hc, (u, v, l) =>
      v > 10 + Hc - 3
        ? snap(M.gold, l + 0.1, u | 0, v | 0)
        : snap(Math.floor(u / 6) % 2 ? M.jade : M.blue, l, u | 0, v | 0),
    );
  const o = 0.55;
  roof(
    px,
    P,
    [-o, -o, n + o, n + o],
    10 + Hc,
    [n / 2, n / 2, n / 2, n / 2],
    10 + Hc + Hr,
    variant ? M.jade : M.slate,
    M.gold,
  );
  const [fx, fy] = P(n / 2, n / 2, 10 + Hc + Hr);
  for (let k = 0; k < 12; k++)
    px.rect(Math.round(fx) - 1, Math.round(fy) - k, 3, 1, snap(M.gold, 0.6 + (k % 3) * 0.1, k, 0, 0));
  for (let b = -3; b <= 3; b++)
    for (let a = -3; a <= 3; a++)
      if (a * a + b * b <= 10) px.set(fx + a, fy - 15 + b, snap(M.gold, 0.95 - (a + b) * 0.08, a, b, 0.3));
  return finish(px, ax, ay);
}

/** A sprite drawn along x, turned to run along y (its mirror image) when that is its axis. */
function alongAxis(s: Sprite, axis: 'x' | 'y'): Sprite {
  if (axis === 'x') return s;
  const { width: W, height: H } = s.img;
  const m = canvas(W, H);
  const g = ctx2d(m);
  g.translate(W, 0);
  g.scale(-1, 1);
  g.drawImage(s.img, 0, 0);
  return { img: m, ax: W - s.ax, ay: s.ay };
}

/** Paifang gate over a road: lacquered pillars on stone bases, painted beams, a gold plaque, a roof. */
function gate(L: number, axis: 'x' | 'y'): Sprite {
  const Hp = 128;
  const W = (L + 1) * 16 + 60;
  const H = Hp + 90;
  const px = new Pix(W, H);
  const ax = 46;
  const ay = Hp + 60;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  const pillar = (x: number) => {
    box(px, P, [x - 0.34, 0.16, x + 0.34, 0.84], 0, 16, ashlar, flags);
    box(px, P, [x - 0.24, 0.26, x + 0.24, 0.74], 16, 20, ashlar, flags);
    post(px, P, x, 0.5, 0.18, 20, Hp, M.lacquer);
    box(px, P, [x - 0.22, 0.28, x + 0.22, 0.72], Hp - 6, Hp - 2, paint(M.gold));
  };
  pillar(0.5);
  const beam = (z0: number, z1: number, y0: number, y1: number, x0: number, x1: number) =>
    box(
      px,
      P,
      [x0, y0, x1, y1],
      z0,
      z1,
      (u, v, l) => {
        if (v > z1 - 1.5 || v < z0 + 1.5) return snap(M.gold, l, u | 0, v | 0);
        const k = Math.floor(u / 7) % 3;
        return snap(k === 0 ? M.jade : k === 1 ? M.blue : M.lacquer, l, u | 0, v | 0);
      },
      paint(M.lacquer),
    );
  beam(Hp - 36, Hp - 26, 0.38, 0.62, 0.35, L - 0.35);
  beam(Hp - 11, Hp, 0.36, 0.64, 0.2, L - 0.2);
  const m2 = L / 2;
  plaque(px, P, 'left', 0.645, m2 - 0.9, m2 + 0.9, Hp - 26, Hp - 11, M.jade);
  pillar(L - 0.5);
  roof(px, P, [-0.3, -0.05, L + 0.3, 1.05], Hp + 1, [0.8, 0.5, L - 0.8, 0.5], Hp + 26, M.slate, M.gold);
  return alongAxis(finish(px, ax, ay), axis);
}

/**
 * Moon gate (月洞門): a garden wall of grey brick on a stone footing under a little tiled coping, a
 * round opening through its middle framed in pale stone, a sill and step, a plaque over the arch.
 * The opening is cut clean through, the wall's thickness seen round its far side.
 */
function moonGate(L: number, axis: 'x' | 'y'): Sprite {
  const Hw = 112;
  const W = (L + 1) * 16 + 60;
  const H = Hw + L * 8 + 80;
  const px = new Pix(W, H);
  const ax = 46;
  const ay = Hw + 52;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  const [y0, y1] = [0.25, 0.75];
  box(px, P, [-0.05, y0 - 0.08, L + 0.05, y1 + 0.08], 0, 8, ashlar, flags);
  box(px, P, [0, y0, L, y1], 8, Hw, brick(Hw), flags);
  const mid = L / 2;
  const ru = 2.2;
  const rz = 46;
  const zc = 48;
  const ring = (y: number, grow: number): Pt[] => {
    const pts: Pt[] = [];
    for (let k = 0; k <= 64; k++) {
      const t = (k / 64) * Math.PI * 2;
      pts.push(P(mid + (ru + grow / 20) * Math.cos(t), y, Math.max(8, zc + (rz + grow) * Math.sin(t))));
    }
    return pts;
  };
  // Pale stone voussoirs round the opening.
  const outer = ring(y1, 7);
  const near = ring(y1, 0);
  const far = ring(y0, 0);
  const [cx, cy] = P(mid, y1, zc);
  fill(px, outer, (x, y) => {
    if (inside(near, x + 0.5, y + 0.5)) return null;
    const a = Math.atan2(y - cy, x - cx);
    const joint = Math.abs(((a / (Math.PI * 2)) * 16 + 16) % 1) < 0.12;
    return snap(M.stone, joint ? 0.4 : 0.82 - (y - cy) * 0.004, x, y);
  });
  // The reveal inside, and the far opening cut clean through.
  fill(px, near, (x, y) => snap(M.brick, 0.3 + (x - cx) * 0.004, x, y));
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++)
      if (inside(near, x + 0.5, y + 0.5) && inside(far, x + 0.5, y + 0.5)) px.clear(x, y);
  // The sill and the step before it.
  const s0 = mid - ru * 0.62;
  const s1 = mid + ru * 0.62;
  box(px, P, [s0, y0, s1, y1], 0, 8, ashlar, flags);
  box(px, P, [s0 - 0.2, y1, s1 + 0.2, y1 + 0.35], 0, 4, ashlar, flags);
  plaque(px, P, 'left', y1, mid - 0.9, mid + 0.9, zc + rz + 6, zc + rz + 22, M.jade);
  roof(px, P, [-0.15, y0 - 0.3, L + 0.15, y1 + 0.3], Hw, [0.1, 0.5, L - 0.1, 0.5], Hw + 16, M.terra);
  return alongAxis(finish(px, ax, ay), axis);
}

/**
 * Two-storey inn (โรงเตี๊ยม): a stone plinth, a ground floor of red posts with wide lattice doors and
 * a signboard, a tiled balcony skirt, an upper storey of plaster and lattice behind a rail, a hip
 * roof, lanterns along both eaves and a wine banner on a pole at the corner.
 */
function inn(n: number): Sprite {
  const H1 = 74;
  const H2 = 62;
  const Hr = 56;
  const W = n * 32 + 64;
  const H = n * 16 + H1 + H2 + Hr + 70;
  const px = new Pix(W, H);
  const ax = W / 2;
  const ay = H1 + H2 + Hr + 56;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  plinth(px, P, [-0.15, -0.15, n + 0.15, n + 0.15], 10);
  for (const side of ['left', 'right'] as const) {
    wall(px, P, side, n, 0, n, 10, H1, plaster(10, H1 + 8));
    for (let u = 0; u <= n; u++)
      wall(px, P, side, n, Math.max(0, u - 0.09), Math.min(n, u + 0.09), 10, H1, (uu, v, l) =>
        snap(M.lacquer, l + (uu % 16 < 1.3 ? 0.18 : -0.05) - under(v, H1 + 8), uu | 0, v | 0),
      );
  }
  const m2 = n / 2;
  wall(px, P, 'right', n, m2 - 1.25, m2 + 1.25, 10, 66, (u, v) => snap(M.dark, 0.4, u | 0, v | 0));
  for (const [a, b] of [
    [m2 - 1.15, m2 - 0.05],
    [m2 + 0.05, m2 + 1.15],
  ] as const)
    lattice(px, P, 'right', n, a, b, 12, 62, 0.62, H1 + 8);
  for (let u = 0; u < n; u++) lattice(px, P, 'left', n, u + 0.22, u + 0.78, 30, 58, 0.5, H1 + 8);
  plaque(px, P, 'right', n, m2 - 1.0, m2 + 1.0, H1 - 6, H1 + 7);
  // The balcony skirt, then the upper storey set back behind it.
  const b = 0.35;
  roof(px, P, [-b, -b, n + b, n + b], H1 + 8, [0, 0, n, n], H1 + 14, M.slate, M.stone, false);
  const i0 = 0.3;
  const i1 = n - 0.3;
  const z0 = H1 + 14;
  const z1 = z0 + H2;
  for (const side of ['left', 'right'] as const) {
    wall(px, P, side, i1, i0, i1, z0, z1, plaster(z0, z1));
    for (let u = 0; u < n - 1; u++)
      lattice(px, P, side, i1, i0 + u + 0.25, i0 + u + 0.85, z0 + 12, z1 - 12, 0.5, z1);
    for (let u = 0; u <= n; u++) {
      const at = i0 + ((i1 - i0) * u) / n;
      wall(px, P, side, i1, at - 0.06, at + 0.06, z0, z1, boards(M.timber, 32, false, z1));
    }
  }
  // The rail round the balcony: a top rail on balusters.
  for (const [side, at] of [
    ['left', n + b],
    ['right', n + b],
  ] as const) {
    wall(px, P, side, at, -b, n + b, z0 + 10, z0 + 13, paint(M.lacquer));
    wall(px, P, side, at, -b, n + b, z0, z0 + 10, (u, v, l) =>
      Math.round(u) % 5 < 2 ? snap(M.lacquer, l - 0.12, u | 0, v | 0) : null,
    );
  }
  const o = 0.7;
  roof(px, P, [-o, -o, n + o, n + o], z1 - 2, [1.3, n / 2, n - 1.3, n / 2], z1 + Hr, M.slate);
  for (const [z, k] of [
    [H1 + 4, 0],
    [z1 - 4, 1],
  ] as const)
    for (let u = 0.6 + k * 0.4; u < n; u += 1.6) {
      lantern(px, P(u, n + b + 0.1, z));
      lantern(px, P(n + b + 0.1, u, z));
    }
  // The wine banner on its pole at the front corner.
  const [qx, qy] = P(n + 0.6, n + 0.6, 0);
  const [x0, yb] = [Math.round(qx), Math.round(qy)];
  for (let y = yb - 150; y < yb; y++) {
    px.set(x0, y, M.wood[1]!);
    px.set(x0 + 1, y, M.wood[3]!);
  }
  for (let y = 0; y < 40; y++)
    for (let x = 0; x < 12; x++) {
      const sway = Math.round(Math.sin(y / 9) * 1.2);
      const l = 0.42 + x * 0.03 + (Math.sin((x + y * 0.3) / 2.2) > 0.6 ? -0.18 : 0);
      const band = y < 3 || (y > 36 && y < 40);
      const glyph = x > 3 && x < 8 && (y - 7) % 8 < 5 && y > 6 && y < 36;
      px.set(
        x0 + 2 + x + sway,
        yb - 146 + y,
        band ? snap(M.red, l, x, y) : glyph ? M.dark[1]! : snap(M.linen, l, x, y),
      );
    }
  return finish(px, ax, ay);
}

/**
 * A wooden boat at its mooring: a planked hull tarred below the waterline, a deck of boards, and a
 * woven awning (variant 1), a mast with its sail furled (variant 2) or a net hung to dry (variant 0).
 */
export function boat(n: number, variant: number): Sprite {
  const W = n * 32 + 40;
  const H = n * 16 + 150;
  const px = new Pix(W, H);
  const ax = W / 2;
  const ay = 120;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  const mid = n / 2;
  const half = 0.85;
  const x0 = 0.3;
  const x1 = n - 0.3;
  const deck = 14;
  const rail = (side: number, zz: number): Pt[] => {
    const pts: Pt[] = [];
    for (let k = 0; k <= 16; k++) {
      const t = k / 16;
      const taper = Math.sin(t * Math.PI);
      pts.push(P(x0 + (x1 - x0) * t, mid + side * half * Math.pow(taper, 0.6), zz + (1 - taper) * 10));
    }
    return pts;
  };
  const near = rail(1, deck);
  const keel = rail(1, -2).map(([x, y]): Pt => [x, y + 4]);
  /** Screen height of a polyline at x (for planks that follow the sheer). */
  const at = (pts: readonly Pt[], x: number): number => {
    for (let k = 0; k < pts.length - 1; k++) {
      const [ax0, ay0] = pts[k]!;
      const [bx0, by0] = pts[k + 1]!;
      if ((x >= ax0 && x <= bx0) || (x <= ax0 && x >= bx0))
        return ay0 + ((by0 - ay0) * (x - ax0)) / (bx0 - ax0 || 1);
    }
    return pts[0]![1];
  };
  const water = rail(1, 4);
  fill(px, [...near, ...[...keel].reverse()], (x, y) => {
    const d = y - at(near, x);
    if (y > at(water, x)) return snap(M.tar, 0.5 - (y - at(water, x)) * 0.05, x, y);
    const strake = Math.floor(d / 4);
    if (d % 4 < 1) return snap(M.deck, 0.2, x, y, 0);
    return snap(M.deck, 0.55 - strake * 0.08 + (valueNoise(x / 6 + strake * 9, y) - 0.5) * 0.2, x, y);
  });
  // The deck between the gunwales.
  fill(px, [...rail(-1, deck), ...[...near].reverse()], (x, y) => {
    const u = x - y * 2;
    if (((u % 9) + 9) % 9 < 1) return snap(M.deck, 0.35, x, y, 0);
    return snap(M.deck, 0.72 + (valueNoise(u / 9, y / 3) - 0.5) * 0.2, x, y);
  });
  for (let k = 0; k < near.length - 1; k++)
    line(px, near[k]!, near[k + 1]!, (_, w) => (w ? M.deck[3]! : M.deck[5]!), 2);
  if (variant === 1) {
    // A woven awning arched over the middle of the deck.
    const a0 = mid - 1.1;
    const a1 = mid + 1.1;
    for (let k = 0; k <= 16; k++) {
      const t = k / 16;
      const h2 = Math.sin(t * Math.PI) * 24;
      const yy = mid - half * 0.8 + half * 1.6 * t;
      line(
        px,
        P(a0, yy, deck + h2),
        P(a1, yy, deck + h2),
        (f) =>
          snap(M.linen, 0.25 + t * 0.4 + (Math.round(f * 40) % 2 ? 0.1 : -0.1), k, Math.round(f * 40), 0.3),
        2,
      );
    }
    line(px, P(a1, mid - half * 0.8, deck), P(a1, mid + half * 0.8, deck), () => M.wood[1]!, 2);
  } else if (variant === 2) {
    const [mx, my] = P(mid, mid, deck);
    for (let y = 0; y < 96; y++) {
      px.set(mx, my - y, M.wood[4]!);
      px.set(mx + 1, my - y, M.wood[2]!);
      px.set(mx + 2, my - y, M.wood[1]!);
    }
    for (let y = 0; y < 6; y++)
      for (let x = -22; x < 24; x++)
        px.set(mx + x, my - 86 + y, snap(M.linen, 0.8 - y * 0.1 + (x % 7 === 0 ? -0.2 : 0), x, y));
    line(px, [mx + 1, my - 96], P(x1, mid, deck + 10), () => M.dark[2]!);
  } else {
    // Poles with a net hung between them to dry, a basket of fish on deck.
    const [ax1, ay1] = P(x0 + 1, mid, deck);
    const [bx1, by1] = P(x1 - 1, mid, deck);
    for (const [qx, qy] of [
      [ax1, ay1],
      [bx1, by1],
    ] as const)
      for (let y = 0; y < 44; y++) {
        px.set(qx, qy - y, M.wood[3]!);
        px.set(qx + 1, qy - y, M.wood[1]!);
      }
    for (let k = 0; k <= 36; k++) {
      const t = k / 36;
      const x = ax1 + (bx1 - ax1) * t;
      const y = ay1 - 42 + (by1 - ay1) * t + Math.sin(t * Math.PI) * 12;
      const len = 18 - Math.round(Math.sin(t * Math.PI) * 8);
      for (let q = 0; q < len; q++)
        if ((q + k) % 3 !== 0 || k % 2 === 0) px.set(x, y + q, snap(M.net, 0.6 - q * 0.02, k, q, 0.3));
    }
    const [kx, ky] = P(mid + 0.5, mid, deck);
    for (let b = -4; b <= 3; b++)
      for (let a = -6; a <= 6; a++)
        if ((a * a) / 36 + (b * b) / 16 <= 1)
          px.set(
            kx + a,
            ky - 4 + b,
            b < -2
              ? snap(M.net, 0.9, a, b)
              : snap(M.deck, 0.6 + a * 0.04 + ((a + b) % 2 ? 0.1 : 0), kx + a, ky + b),
          );
  }
  const c = px.toCanvas();
  selOutline(c, '#120d0a');
  // Ripples on the water round the hull.
  const g = ctx2d(c);
  g.save();
  g.globalCompositeOperation = 'destination-over';
  g.strokeStyle = 'rgba(220,236,240,.5)';
  for (const k of [1, 1.25]) {
    g.beginPath();
    const [cxp, cyp] = P(mid, mid, -3);
    g.ellipse(cxp, cyp, n * 13 * k, n * 4.5 * k, -0.46, 0, Math.PI * 2);
    g.stroke();
  }
  g.restore();
  return { img: c, ax, ay };
}

/** The sprite for one of the map's buildings (not terraces, which `terraces.ts` draws). */
export function buildingSprite(
  kind: 'house' | 'pagoda' | 'pavilion' | 'gate' | 'inn' | 'boat' | 'moongate',
  w: number,
  h: number,
  variant: number,
  axis: 'x' | 'y' = 'x',
): Sprite {
  switch (kind) {
    case 'house':
      return house(w, variant);
    case 'pagoda':
      return pagoda(w);
    case 'pavilion':
      return pavilion(w, variant);
    case 'inn':
      return inn(w);
    case 'boat':
      return boat(w, variant);
    case 'moongate':
      return moonGate(Math.max(w, h), axis);
    case 'gate':
      return gate(Math.max(w, h), axis);
  }
}
